// ============================================================================
// TrendsSection — month-by-month trends: sessions delivered, hours coached, and
// income billed per currency. Each trend is an animated SVG TrendChart that
// draws on scroll. Income trends are rendered per-currency (one chart per entry
// in `trends.income_by_currency`), ordered to match the income section.
// The income charts are rendered only when the income section is present (had
// at least one fee). Each sub-chart appears only when its series has points.
// ============================================================================
import { useTranslation } from "react-i18next";
import { ReportSection } from "./ReportSection";
import { TrendChart } from "../charts/TrendChart";
import type { ChartAccent } from "../charts/TrendChart";
import type { TypedBusinessReport } from "../types";
import { hasIncome } from "../types";
import { fmtNum, fmtHours, fmtMoney } from "../format";
import type { SeriesPoint } from "../../../api";

interface TrendSpec {
  key: string;
  label: string;
  accent: ChartAccent;
  series: SeriesPoint[];
  format: (v: number) => string;
}

export function TrendsSection({
  report,
  index,
}: {
  report: TypedBusinessReport;
  index: number;
}) {
  const { t, i18n } = useTranslation("businessReport");
  const locale = i18n.language;
  const { trends, income } = report;

  const specs: TrendSpec[] = [];

  if (trends.sessions.length > 0) {
    specs.push({
      key: "sessions",
      label: t("trends.sessions"),
      accent: "coral",
      series: trends.sessions,
      format: (v) => fmtNum(v, 0, locale),
    });
  }
  if (trends.hours.length > 0) {
    specs.push({
      key: "hours",
      label: t("trends.hours"),
      accent: "ocean",
      series: trends.hours,
      format: (v) => `${fmtHours(v, locale)}${t("units.hoursSuffix")}`,
    });
  }

  // Income: one chart per currency, ordered by the income section's block order
  // so the presentation stays consistent between sections.
  if (hasIncome(income)) {
    income.by_currency.forEach((blk) => {
      const series = trends.income_by_currency[blk.currency];
      if (!series || series.length === 0) return;
      specs.push({
        key: `income-${blk.currency}`,
        label:
          income.by_currency.length > 1
            ? `${t("trends.income")} (${blk.currency})`
            : t("trends.income"),
        accent: "lime",
        series,
        format: (v) => fmtMoney(v, blk.currency, locale, blk.currency_symbol),
      });
    });
  }

  return (
    <ReportSection
      id="business-trends"
      index={index}
      kicker={t("kicker")}
      title={t("trends.title")}
      subtitle={t("trends.subtitle")}
      hasData={specs.length > 0}
    >
      <div className="space-y-8">
        {specs.map((spec) => (
          <div key={spec.key}>
            <h3 className="mb-2 font-display text-sm font-bold uppercase tracking-kicker text-body-soft">
              {spec.label}
            </h3>
            <TrendChart
              series={spec.series}
              accent={spec.accent}
              formatValue={spec.format}
              ariaLabel={t("trends.chartAria", { metric: spec.label })}
              locale={locale}
            />
          </div>
        ))}
      </div>
    </ReportSection>
  );
}
